import type {ReactElement} from 'react';
import { useRef} from 'react';
import {Panel, PanelContent} from '@Components/panel';
import type {BasePanel} from '@Components/panel/panel.types';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import type { FlatIconType} from '@Components/icon-v2/icon.types';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import { useContainerQuery} from '@Hooks/useContainerQuery';
import styles from './status-panel.module.scss';

/**
 * @author Taimur Salman <taimursalman@250mils.com>
 */
export enum StatusPanelMode {
  SAVING = 'SAVING',
  PUBLISHING = 'PUBLISHING',
  SCHEDULING = 'SCHEDULING',
  ERROR = 'ERROR',
}

interface StatusPanelProps extends BasePanel {
  theme?: StatusPanelMode;
  isLoading?: boolean;

  // Text to be displayed in the panel when it is Loaded. It is EXTREMELY important that this string is escaped properly
  escapedPanelTextString?: string;
  // Text to be displayed in the panel when it is Loaded. It is EXTREMELY important that this string is escaped properly
  escapedPanelSubtextString?: string;

  // Text to be displayed when the panel is in the Loading state. It is EXTREMELY important that this string is escaped properly.
  escapedLoadingPanelTextString?: string;

  leftButton?: ReactElement<any>;
  rightButton?: ReactElement<any>;
  panelIcon?: string;
}

const containerQuery: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 400,
  },
  large: {
    minWidth: 401,
  },
};

export function StatusPanel({
  isLoading = false,
  theme = StatusPanelMode.SCHEDULING,
  escapedLoadingPanelTextString = '',
  escapedPanelTextString = '',
  escapedPanelSubtextString = '',
  panelIcon = 'icon-exclamation-triangle',
  ...props
}: StatusPanelProps): ReactElement<any> {
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);

  const getButtons = (): ReactElement<any> | null => {
    return (
      <div className={`${styles.buttonContainer}  ${params.small ? 'flex-v-row' : 'flex-justify-between'}`}>
        {props.leftButton}
        {props.rightButton}
      </div>
    );
  };

  const getFlatIconType = (): FlatIconType => {
    if (theme === StatusPanelMode.PUBLISHING) {
      return {color: FlatIconColor.SUCCESS, type: FlatIconColorType.DARK};
    }
    if (theme === StatusPanelMode.SCHEDULING) {
      return {color: FlatIconColor.PRIMARY, type: FlatIconColorType.DARK};
    }
    if (theme === StatusPanelMode.SAVING) {
      return {color: FlatIconColor.NEUTRAL, type: FlatIconColorType.DARK};
    }
    return {color: FlatIconColor.DANGER, type: FlatIconColorType.DARK};
  };

  const getPanelColorClass = (): string => {
    if (theme === StatusPanelMode.SCHEDULING) {
      return styles.primaryPanel;
    }
    if (theme === StatusPanelMode.PUBLISHING) {
      return styles.successPanel;
    }
    if (theme === StatusPanelMode.SAVING) {
      return styles.neutralPanel;
    }
    return styles.dangerPanel;
  };

  const getSubtextElement = (): ReactElement<any> | null => {
    if (escapedPanelSubtextString) {
      return <Text size={TextSize.XSMALL} className={`spacing-m-r-2 ${styles.textCenterAlign} ${styles.subText}`} dangerouslySetInnerHTML val={escapedPanelSubtextString} />;
    }

    return null;
  };

  const getLoadedContent = (): ReactElement<any> => {
    return (
      <PanelContent>
        <div className={`${getPanelColorClass()} ${styles.statusPanelBase}`}>
          <div className={styles.panelUpperPart}>
            <div className={styles.iconWrapper}>
              <Icon icon={panelIcon} size={IconSize.SIZE_ICON_24} type={IconType.FLAT} shape={IconShape.CIRCLE} flatIconType={getFlatIconType()} />
            </div>
            <Text className={`${styles.textCenterAlign} body-lg-regular`} dangerouslySetInnerHTML val={escapedPanelTextString} />
            {getSubtextElement()}
          </div>
          {getButtons()}
        </div>
      </PanelContent>
    );
  };

  const getLoadingText = (): string => {
    if (escapedLoadingPanelTextString) {
      return escapedLoadingPanelTextString;
    }
    if (theme === StatusPanelMode.SCHEDULING) {
      return window.i18next.t('pmwjs_scheduling');
    }
    if (theme === StatusPanelMode.PUBLISHING) {
      return window.i18next.t('pmwjs_publishing');
    }
    if (theme === StatusPanelMode.SAVING) {
      return window.i18next.t('pmwjs_saving');
    }
    return '';
  };

  const getLoadingIconColor = (): LOADER_COLOR => {
    return theme === StatusPanelMode.SAVING ? LOADER_COLOR.DARK : LOADER_COLOR.PRIMARY;
  };

  const getLoadingContent = (): ReactElement<any> => {
    return (
      <PanelContent>
        <div className={`${styles.loadingBox} ${styles.statusPanelBase} ${theme === StatusPanelMode.SAVING ? styles.neutralPanel : styles.primaryPanel}`}>
          <div className={styles.statusPanelLoadingContent}>
            <CircularProgressLoader classes="_unmargin" color={getLoadingIconColor()} size={LOADER_SIZE.MEDIUM} />
            <Text className={`${styles.textCenterAlign} body-lg-regular`} dangerouslySetInnerHTML size={TextSize.SMALL} val={getLoadingText()} />
            {getSubtextElement()}
          </div>
        </div>
      </PanelContent>
    );
  };

  if (isLoading) {
    return <Panel ref={containerRef} panelContent={getLoadingContent()} />;
  }
  return <Panel ref={containerRef} panelContent={getLoadedContent()} />;
}
